import React, { useLayoutEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useGSAP } from '@gsap/react';
import Header from './components/Header';
import Home from './components/Home';
import Heritage from './components/Heritage';
import Products from './components/Products';
import ProductDetails from './components/ProductDetails';
import ServicesPage from './components/ServicesPage';
import ContactPage from './components/ContactPage';
import AdminPage from './components/AdminPage';
import CustomCursor from './components/CustomCursor';
import PageLoader from './components/PageLoader';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const App: React.FC = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    // Disable browser's automatic scroll restoration to prevent conflicts
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }

    // Immediate scroll reset
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useGSAP(() => {
    // Trigger refresh when location changes to ensure ScrollTriggers recalculate
    // slightly delayed to allow React to render the new route
    // Increased delay to 500ms to allow for image loading and layout shifts
    const timer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 500);
    return () => clearTimeout(timer);
  }, { dependencies: [location.pathname] });

  const isAdmin = location.pathname.startsWith('/admin-dman');

  const [isLoading, setIsLoading] = React.useState(true);

  // Lock body scroll while loading
  React.useEffect(() => {
    if (isLoading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      // Refresh ScrollTrigger after loader animation completes
      ScrollTrigger.refresh();
    }
  }, [isLoading]);

  return (
    <div className={`${isAdmin ? 'bg-gray-100 text-gray-900' : 'bg-diwan-900 selection:bg-diwan-gold selection:text-white cursor-none text-diwan-cream'} min-h-screen relative`}>
      {isLoading && <PageLoader onLoadingComplete={() => setIsLoading(false)} />}

      {!isAdmin && <CustomCursor />}
      {!isAdmin && <Header />}

      <main className={isAdmin ? "" : "w-full min-h-screen"}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<Heritage />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetails />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/admin-dman" element={<AdminPage />} />
        </Routes>
      </main>

      {!isAdmin && (
        <footer className="py-8 text-center text-diwan-cream/60 font-sans text-xs uppercase tracking-widest border-t border-diwan-700">
          © {new Date().getFullYear()} D'mans Furniture (Ranbir Singh Dhiman & Sons). All Rights Reserved.
        </footer>
      )}
    </div>
  );
};

export default App;